import type { UserProfile } from "shared/interfaces/api/auth";
import type { CompanyToken, Token } from "shared/utils/auth";

/** CamelCase — matches axios `toCamelCaseKeys` on `/user/profile/` (PRODUCT-1685). */
export type CustomerioIdentifyAttributes = {
  id: string;
  email: string;
  name: string;
  role: string;
  companySlug: string;
  companyName: string;
  /** Unix seconds — user signup (`/user/profile/` or JWT), not `iat` */
  createdAt: number;
  userHash: string;
};

export type CustomerioIdentifyBuildInput = {
  claims: Token | null | undefined;
  activeUser: UserProfile | null | undefined;
  company: CompanyToken | null | undefined;
  managerRole?: string;
};

/**
 * Builds the Customer.io identify payload from backend `/user/profile/`
 * (`customerio_hash` from PRODUCT-1685) plus manager profile and JWT claims.
 */
export const buildCustomerioIdentifyAttributes = (
  input: CustomerioIdentifyBuildInput
): CustomerioIdentifyAttributes | null => {
  const { claims, activeUser, company, managerRole } = input;

  const id = claims?.sub;
  const email = activeUser?.email ?? claims?.email ?? undefined;
  const userHash = activeUser?.customerioHash;
  const profileCreatedAt = activeUser?.createdAt;
  const claimCreatedAt = claims?.createdAt;
  const createdAt =
    typeof profileCreatedAt === "number" && Number.isFinite(profileCreatedAt)
      ? profileCreatedAt
      : typeof claimCreatedAt === "number" && Number.isFinite(claimCreatedAt)
        ? claimCreatedAt
        : undefined;

  const firstName = activeUser?.firstName ?? claims?.givenName;
  const lastName = activeUser?.lastName ?? claims?.familyName;
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();

  const companySlug = company?.slug;
  const companyName = company?.name;
  const role = managerRole;

  if (
    !id ||
    !email ||
    !userHash ||
    !companySlug ||
    !companyName ||
    !role ||
    createdAt == null ||
    !name
  ) {
    return null;
  }

  return {
    id,
    email,
    name,
    role,
    companySlug,
    companyName,
    createdAt,
    userHash,
  };
};
